import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { startLoadingComercioPosms } from '../actions/sucursales';
import {
    RESOLUCION_CARGANDO,
    RESOLUCION_ERROR,
    RESOLUCION_RESUELTA,
} from '../helpers/cobroDatafono';

/**
 * EX-619: comercio POSMS guardado de una sucursal, para la ficha de configuración.
 *
 * Es una lectura aparte y no un campo más de la sucursal del store porque el endpoint que puebla
 * ese store es público (EX-618): el comercio solo viaja por `GET /api/sucursales/:id/comercio-posms`
 * y solo hacia un rol de plataforma.
 *
 * Devuelve la resolución además del valor por el mismo motivo que `useDatafonosSucursal`: "esta
 * sucursal no tiene comercio" y "todavía no lo sé" pintan pantallas distintas, y colapsarlas en la
 * cadena vacía haría parpadear el aviso de "sin comercio" cada vez que se abre una sucursal que sí
 * lo tiene.
 *
 * @param {string} sucursalId - sucursal que se está editando; vacío en un alta, que no tiene nada
 *   que leer.
 * @param {boolean} activo - si el rol de quien mira puede ver el comercio.
 */
export const useComercioPosms = (sucursalId, activo) => {
    const dispatch = useDispatch();
    const [resolucion, setResolucion] = useState(RESOLUCION_CARGANDO);
    const [comercio, setComercio] = useState('');
    // Solo manda la última petición pedida: al saltar de una sucursal a otra, una respuesta que
    // llegue tarde no puede pintar el comercio de la anterior.
    const peticionVigenteRef = useRef(0);

    const cargar = useCallback(async () => {
        const peticion = peticionVigenteRef.current + 1;
        peticionVigenteRef.current = peticion;
        setResolucion(RESOLUCION_CARGANDO);

        const respuesta = await dispatch(startLoadingComercioPosms(sucursalId));
        if (peticionVigenteRef.current !== peticion) return;

        if (!respuesta?.ok) {
            setComercio('');
            setResolucion(RESOLUCION_ERROR);
            return;
        }
        setComercio(respuesta.posmsMerchantUuid || '');
        setResolucion(RESOLUCION_RESUELTA);
    }, [dispatch, sucursalId]);

    useEffect(() => {
        // Un alta no tiene comercio que leer, y para un rol de negocio no hay nada que pedir: en
        // los dos casos la resolución es correcta con el comercio vacío, no un error.
        if (!activo || !sucursalId) {
            peticionVigenteRef.current += 1;
            setComercio('');
            setResolucion(RESOLUCION_RESUELTA);
            return;
        }
        cargar();
    }, [activo, sucursalId, cargar]);

    return { resolucion, comercio, reintentar: cargar };
};
